import React, { useImperativeHandle, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import GeneralStatIndicatorPortlet from '@ui/portlet/GeneralStatIndicatorPortlet';
import Tag from '@ui/tag/Tag';
import Disclosure from '@ui/disclosure/Disclosure';
import Message from '@ui/message/Message';
import TablePlaceholder from '@ui/placeholder/TablePlaceholder';
import { Integration } from '@app/model/integration-settings/Integration';
import { useIntegrationRuns } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import {
	formatDayTime,
	formatDuration,
	INTERVAL_LABEL,
	PROVIDER_LABEL,
	RESULT_TAG,
	TYPE_LABEL,
} from '../IntegrationSettings.utilities';
import { StatusTag } from '../card/IntegrationHeaderActions';

export type IntegrationLogDrawerHandle = { open: (integration: Integration) => void };

const ICON = 'h-5 w-5 text-gray-400';

// The log of process runs and manual catalogue refreshes of one Integration (decision 9): 90 days, statistics of 7 and 30 days, one row per
// run, and the technical details with the credential removed.
const IntegrationLogDrawer = React.forwardRef<IntegrationLogDrawerHandle, { hotelName: string }>(({ hotelName }, ref) => {
	const drawer = useDrawer();
	const [integration, setIntegration] = useState<Integration | null>(null);
	const { data, isLoading, isError } = useIntegrationRuns(integration?.id ?? null);

	useImperativeHandle(ref, () => ({
		open: (opened: Integration) => {
			setIntegration(opened);
			drawer.open();
		},
	}));

	const title = integration === null ? 'Log' : `${hotelName} · ${TYPE_LABEL[integration.type]} log`;
	return <Drawer title={title} size="large" ref={drawer.ref}>
		{integration !== null && <div className="px-4 py-6 sm:px-6">
			<div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
				<StatusTag integration={integration} />
				<span>
					{integration.provider === null ? '' : PROVIDER_LABEL[integration.provider]}
					{integration.interval === null ? '' : ` · ${INTERVAL_LABEL[integration.interval]} from ${integration.startAt ?? '—'}`}
					{' '}· Last run {formatDayTime(integration.lastRunAt)} · Next run {formatDayTime(integration.nextRunAt)}
				</span>

			</div>
			{isLoading && <div className="mt-6"><TablePlaceholder /></div>}
			{isError && <div className="mt-6"><Message state="error" title="The log did not load">Close the log and open it again.</Message></div>}
			{data !== undefined && <>
				<div className="mt-6 grid grid-cols-2 gap-3">
					<GeneralStatIndicatorPortlet title="Runs (7 days)" stat={String(data.runs7Days)} icon={<ListBulletIcon className={ICON} />} />
					<GeneralStatIndicatorPortlet title="Failed (7 days)" stat={String(data.failed7Days)} icon={<ExclamationTriangleIcon className={ICON} />} />
					<GeneralStatIndicatorPortlet title="Partly failed (7 days)" stat={String(data.partlyFailed7Days)} icon={<CheckCircleIcon className={ICON} />} />
					<GeneralStatIndicatorPortlet title="Runs (30 days)" stat={String(data.runs30Days)} icon={<ClockIcon className={ICON} />} />
				</div>
				{data.runs.length === 0 && <div className="mt-6"><Message state="info">No run yet.</Message></div>}
				{data.runs.length > 0 && <div className="relative mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
					<table className="min-w-full divide-y divide-gray-300">
						<thead>
						<tr>
							<th scope="col" className="py-2.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">Started</th>
							<th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">Result</th>
							<th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-gray-900">Message</th>
							<th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-gray-900">Duration</th>
						</tr>
						</thead>
						<tbody className="divide-y divide-gray-200">
						{data.runs.map(run => <React.Fragment key={run.id}>
							<tr>
								<td className="whitespace-nowrap py-2.5 pl-4 pr-3 align-top text-xs font-medium text-gray-900 sm:pl-6">{formatDayTime(run.startedAt)}</td>
								<td className="whitespace-nowrap px-3 py-2.5 align-top text-xs"><Tag state={RESULT_TAG[run.result].state}>{RESULT_TAG[run.result].label}</Tag></td>
								<td className="px-3 py-2.5 align-top text-xs text-gray-900">
                                    {run.trigger === 'CATALOGUE' && <span className="mb-1 block font-semibold">Room catalogue</span>}
									{run.message}
									{run.technical !== null && <div className="mt-2">
										<Disclosure label="Technical details" size="small">
											<p className="whitespace-pre-wrap break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700">
												{run.technical.requestId === null ? '' : `Request ${run.technical.requestId} · `}
                                                {run.technical.httpStatus === null ? '' : `HTTP ${run.technical.httpStatus} · `}
                                                {run.technical.providerCode === null ? '' : `Provider ${run.technical.providerCode} · `}{run.technical.text}
											</p>
										</Disclosure>
									</div>}
								</td>
								<td className="whitespace-nowrap px-3 py-2.5 text-right align-top text-xs text-gray-500">{formatDuration(run.durationSeconds)}</td>
							</tr>
						</React.Fragment>)}
						</tbody>
					</table>
				</div>}
			</>}
		</div>}
	</Drawer>;
});

export default IntegrationLogDrawer;
